import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SavedFiltersSidebar } from '@/components/SavedFiltersSidebar';
import { advanceAPI, employeeAPI, organizationAPI } from '@/lib/api';
import { formatCurrency, getMonthOptions, getCurrentMonth, getCurrentYear } from '@/lib/utils';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  CreditCard,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Pause,
  Download,
  Play,
  Search,
  Filter,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Calendar,
  History,
  Bookmark,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300', icon: Play },
  paused: { label: 'Paused', color: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300', icon: Pause },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300', icon: CheckCircle },
};

const ADVANCE_TYPES = [
  { value: 'one_time', label: 'One-Time Deduction' },
  { value: 'installment', label: 'Monthly Installment' },
];

export default function AdvancesPage() {
  const { isAdminLevel } = useAuth();
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteHasDeductions, setDeleteHasDeductions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Convert dialog state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertingAdvance, setConvertingAdvance] = useState(null);
  const [convertData, setConvertData] = useState({
    advance_type: 'installment',
    monthly_deduction: '',
    num_installments: '',
  });

  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrgs, setFilterOrgs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Saved Filters Sidebar
  const [savedFiltersOpen, setSavedFiltersOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    employee_id: '',
    name: '',
    advance_type: 'one_time',
    total_amount: '',
    monthly_deduction: '',
    num_installments: '',
    start_month: getCurrentMonth().toString(),
    start_year: getCurrentYear().toString(),
    remarks: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [advancesRes, employeesRes, orgsRes] = await Promise.all([
        advanceAPI.getAll(),
        employeeAPI.getAll(),
        organizationAPI.getAll(),
      ]);
      setAdvances(advancesRes.data);
      setEmployees(employeesRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (id) => employees.find(e => e.id === id)?.name || '';
  const getEmployeeCode = (id) => employees.find(e => e.id === id)?.employee_id || '';
  const getEmployeeOrg = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? organizations.find(o => o.id === emp.organization_id)?.name || '' : '';
  };

  // Get current filters for saving
  const getCurrentFilters = () => ({
    status: filterStatus,
    organizations: filterOrgs,
  });

  // Apply saved filter
  const handleApplyFilter = (filterConfig) => {
    if (filterConfig.status) setFilterStatus(filterConfig.status);
    if (filterConfig.organizations) setFilterOrgs(filterConfig.organizations);
  };

  // Filter labels for display in sidebar
  const filterLabels = {
    status: 'Status',
    organizations: 'Organizations',
  };

  const filteredAdvances = advances.filter(adv => {
    if (filterStatus !== 'all' && adv.status !== filterStatus) return false;
    if (filterOrgs.length > 0) {
      const emp = employees.find(e => e.id === adv.employee_id);
      if (!emp || !filterOrgs.includes(emp.organization_id)) return false;
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const empName = getEmployeeName(adv.employee_id).toLowerCase();
      const empCode = getEmployeeCode(adv.employee_id).toLowerCase();
      if (!empName.includes(query) && !empCode.includes(query) && !adv.name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    return true;
  });

  const handleOpenDialog = (advance = null) => {
    if (advance) {
      setEditingAdvance(advance);
      setFormData({
        employee_id: advance.employee_id,
        name: advance.name,
        advance_type: advance.advance_type,
        total_amount: advance.total_amount?.toString() || '',
        monthly_deduction: advance.monthly_deduction?.toString() || '',
        num_installments: advance.num_installments?.toString() || '',
        start_month: advance.start_month?.toString() || getCurrentMonth().toString(),
        start_year: advance.start_year?.toString() || getCurrentYear().toString(),
        remarks: advance.remarks || '',
      });
    } else {
      setEditingAdvance(null);
      setFormData({
        employee_id: '',
        name: '',
        advance_type: 'one_time',
        total_amount: '',
        monthly_deduction: '',
        num_installments: '',
        start_month: getCurrentMonth().toString(),
        start_year: getCurrentYear().toString(),
        remarks: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.employee_id || !formData.name || !formData.total_amount) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        employee_id: formData.employee_id,
        name: formData.name,
        advance_type: formData.advance_type,
        total_amount: parseFloat(formData.total_amount),
        start_month: parseInt(formData.start_month),
        start_year: parseInt(formData.start_year),
        remarks: formData.remarks || null,
      };

      if (formData.advance_type === 'installment') {
        if (formData.monthly_deduction) {
          payload.monthly_deduction = parseFloat(formData.monthly_deduction);
        } else if (formData.num_installments) {
          payload.num_installments = parseInt(formData.num_installments);
        } else {
          toast.error('Please specify monthly deduction or number of installments');
          setSubmitting(false);
          return;
        }
      }

      if (editingAdvance) {
        await advanceAPI.update(editingAdvance.id, {
          name: payload.name,
          monthly_deduction: payload.monthly_deduction,
          remarks: payload.remarks,
        });
        toast.success('Advance updated successfully');
      } else {
        await advanceAPI.create(payload);
        toast.success('Advance created successfully');
      }

      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePause = async (id) => {
    try {
      await advanceAPI.pause(id);
      toast.success('Advance paused');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause advance');
    }
  };

  const handleResume = async (id) => {
    try {
      await advanceAPI.resume(id);
      toast.success('Advance resumed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume advance');
    }
  };

  const handleDelete = async (force = false) => {
    if (!deleteId) return;
    try {
      const response = await advanceAPI.delete(deleteId, force);
      toast.success(response.data?.message || 'Advance deleted');
      setDeleteId(null);
      setDeleteHasDeductions(false);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      // Check if error is about existing deductions
      if (detail.includes('existing deduction') && !force) {
        setDeleteHasDeductions(true);
      } else {
        toast.error(detail || 'Failed to delete advance');
        setDeleteId(null);
        setDeleteHasDeductions(false);
      }
    }
  };

  const handleOpenConvertDialog = (advance) => {
    setConvertingAdvance(advance);
    // Default to opposite type
    const newType = advance.advance_type === 'one_time' ? 'installment' : 'one_time';
    setConvertData({
      advance_type: newType,
      monthly_deduction: '',
      num_installments: '',
    });
    setConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    if (!convertingAdvance) return;
    
    setSubmitting(true);
    try {
      const payload = {
        advance_type: convertData.advance_type,
      };
      
      if (convertData.advance_type === 'installment') {
        if (convertData.monthly_deduction) {
          payload.monthly_deduction = parseFloat(convertData.monthly_deduction);
        } else if (convertData.num_installments) {
          payload.num_installments = parseInt(convertData.num_installments);
        } else {
          toast.error('Please specify monthly deduction or number of installments');
          setSubmitting(false);
          return;
        }
      }
      
      await advanceAPI.convert(convertingAdvance.id, payload);
      toast.success('Advance converted successfully');
      setConvertDialogOpen(false);
      setConvertingAdvance(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert advance');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate totals
  const totals = filteredAdvances.reduce((acc, adv) => ({
    totalAmount: acc.totalAmount + (adv.total_amount || 0),
    totalDeducted: acc.totalDeducted + (adv.total_deducted || 0),
    totalRemaining: acc.totalRemaining + (adv.remaining_balance || 0),
  }), { totalAmount: 0, totalDeducted: 0, totalRemaining: 0 });

  // CSV Download function
  const handleDownloadCSV = () => {
    const headers = ['Employee Name', 'Employee Code', 'Advance Name', 'Type', 'Total Amount', 'Deducted', 'Remaining', 'Last Deduction Date', 'Status', 'Start Month', 'Remarks'];
    const rows = filteredAdvances.map(adv => {
      return [
        adv.employee_name || '',
        adv.employee_code || '',
        adv.name || '',
        adv.advance_type === 'one_time' ? 'One-Time' : 'Installment',
        adv.total_amount || 0,
        adv.total_deducted || 0,
        adv.remaining_balance || 0,
        adv.last_deduction_date ? new Date(adv.last_deduction_date).toLocaleDateString('en-IN') : '',
        adv.status || '',
        `${monthOptions.find(m => m.value === adv.start_month)?.label || ''} ${adv.start_year || ''}`,
        adv.remarks || ''
      ];
    });
    
    // Add totals row
    rows.push([]);
    rows.push(['TOTALS', '', '', '', totals.totalAmount, totals.totalDeducted, totals.totalRemaining, '', '', '', '']);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `advances_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success('CSV downloaded successfully');
  };

  const handleDownloadPDF = () => {
    const dateStr = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Build table rows HTML
    let tableRowsHTML = filteredAdvances.map(adv => {
      const statusConfig = STATUS_CONFIG[adv.status] || STATUS_CONFIG.active;
      return `
        <tr>
          <td>${adv.employee_name || getEmployeeName(adv.employee_id)}</td>
          <td>${adv.employee_code || getEmployeeCode(adv.employee_id)}</td>
          <td>${adv.name || ''}</td>
          <td>${adv.advance_type === 'one_time' ? 'One-Time' : 'Installment'}</td>
          <td class="right">${formatCurrency(adv.total_amount || 0)}</td>
          <td class="right">${formatCurrency(adv.total_deducted || 0)}</td>
          <td class="right">${formatCurrency(adv.remaining_balance || 0)}</td>
          <td><span class="status-${adv.status}">${statusConfig.label}</span></td>
        </tr>
      `;
    }).join('');

    const filterInfo = [];
    if (filterStatus !== 'all') filterInfo.push(`Status: ${STATUS_CONFIG[filterStatus]?.label || filterStatus}`);
    if (filterOrgs.length > 0) filterInfo.push(`${filterOrgs.length} organization(s) selected`);

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Advances Report - PayTrack</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 40px; color: #333; }
          .header { border-bottom: 2px solid #e5e7eb; padding-bottom: 20px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; color: #111; }
          .header p { color: #666; font-size: 14px; margin-top: 4px; }
          .filters { background: #f9fafb; padding: 12px; border-radius: 6px; margin-bottom: 20px; font-size: 13px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th, td { padding: 8px 6px; text-align: left; border-bottom: 1px solid #e5e7eb; }
          th { background: #f3f4f6; font-weight: 600; color: #374151; }
          .right { text-align: right; }
          .totals { background: #f0f9ff; font-weight: 600; }
          .status-active { color: #059669; font-weight: 500; }
          .status-paused { color: #d97706; font-weight: 500; }
          .status-completed { color: #2563eb; font-weight: 500; }
          .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #999; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Advances Report</h1>
          <p>Generated on ${dateStr} • ${filteredAdvances.length} advances</p>
        </div>
        ${filterInfo.length > 0 ? `<div class="filters">Filters: ${filterInfo.join(' | ')}</div>` : ''}
        <table>
          <thead>
            <tr>
              <th>Employee Name</th>
              <th>Employee Code</th>
              <th>Advance Name</th>
              <th>Type</th>
              <th class="right">Total Amount</th>
              <th class="right">Deducted</th>
              <th class="right">Remaining</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHTML}
          </tbody>
          <tfoot>
            <tr class="totals">
              <td colspan="4"><strong>Total (${filteredAdvances.length} advances)</strong></td>
              <td class="right">${formatCurrency(totals.totalAmount)}</td>
              <td class="right">${formatCurrency(totals.totalDeducted)}</td>
              <td class="right">${formatCurrency(totals.totalRemaining)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
        <div class="footer">PayTrack - Salary Reporting System</div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
    toast.success('PDF export ready for printing');
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '-';
    }
  };

  const monthOptions = getMonthOptions();
  const yearOptions = Array.from({ length: 5 }, (_, i) => getCurrentYear() + i - 1);

  return (
    <DashboardLayout>
      <div data-testid="advances-page" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Advances</h1>
            <p className="text-muted-foreground mt-1">Manage employee salary advances and deductions</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadCSV} data-testid="download-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button onClick={() => handleOpenDialog()} data-testid="add-advance-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Advance
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 p-4 bg-muted/30 rounded-lg">
          <div className="relative flex-grow min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employee or advance..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-advance-input"
            />
          </div>

          <div className="w-[200px]">
            <MultiSelect
              options={organizations.map(org => ({ value: org.id, label: org.name }))}
              selected={filterOrgs}
              onChange={setFilterOrgs}
              placeholder="All Organizations"
              data-testid="filter-org-select"
            />
          </div>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[130px]" data-testid="filter-status-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="default"
            onClick={() => setSavedFiltersOpen(true)}
            data-testid="open-saved-filters-btn"
          >
            <Bookmark className="h-4 w-4 mr-2" />
            Saved Filters
          </Button>
        </div>

        {/* Saved Filters Sidebar */}
        <SavedFiltersSidebar
          isOpen={savedFiltersOpen}
          onClose={() => setSavedFiltersOpen(false)}
          page="advances"
          currentFilters={getCurrentFilters()}
          onApplyFilter={handleApplyFilter}
          filterLabels={filterLabels}
        />

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Advances ({filteredAdvances.length})
            </CardTitle>
            <CardDescription>Track and manage salary advances</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredAdvances.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <CreditCard className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No advances found</p>
                <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Advance
                </Button>
              </div>
            ) : (
              <div className="overflow-auto" style={{ maxHeight: '500px' }}>
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Advance Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead className="text-right">Deducted</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead>Last Deduction</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAdvances.map((advance) => (
                      <TableRow key={advance.id} data-testid={`advance-row-${advance.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{getEmployeeName(advance.employee_id)}</p>
                            <p className="text-xs text-muted-foreground">{getEmployeeCode(advance.employee_id)}</p>
                          </div>
                        </TableCell>
                        <TableCell>{advance.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {advance.advance_type?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(advance.total_amount)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {advance.total_deducted > 0 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-auto py-1 px-2 font-mono text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950">
                                  {formatCurrency(advance.total_deducted)}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80" align="end">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <History className="h-4 w-4 text-blue-500" />
                                    <h4 className="font-semibold">Deduction History</h4>
                                  </div>
                                  <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {advance.deduction_history?.map((ded, idx) => (
                                      <div key={idx} className="flex justify-between items-start text-sm border-b pb-2 last:border-0">
                                        <div>
                                          <p className="font-medium">{monthOptions.find(m => m.value === ded.month)?.label} {ded.year}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {formatDate(ded.created_at)}
                                            {ded.is_manual_override && <span className="ml-1 text-amber-600">(Manual)</span>}
                                          </p>
                                        </div>
                                        <div className="text-right">
                                          <p className="font-mono text-emerald-600">-{formatCurrency(ded.deducted_amount)}</p>
                                          <p className="text-xs text-muted-foreground">
                                            Bal: {formatCurrency(ded.remaining_balance_after)}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="pt-2 border-t flex justify-between font-semibold">
                                    <span>Total Deducted</span>
                                    <span className="font-mono text-emerald-600">{formatCurrency(advance.total_deducted)}</span>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          <span className={advance.remaining_balance > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600'}>
                            {formatCurrency(advance.remaining_balance)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {advance.last_deduction_date ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="h-3 w-3 text-muted-foreground" />
                              {formatDate(advance.last_deduction_date)}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(advance.status)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {advance.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePause(advance.id)}
                                title="Pause"
                              >
                                <Pause className="h-4 w-4 text-amber-600" />
                              </Button>
                            )}
                            {advance.status === 'paused' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResume(advance.id)}
                                title="Resume"
                              >
                                <Play className="h-4 w-4 text-emerald-600" />
                              </Button>
                            )}
                            {advance.status !== 'completed' && advance.remaining_balance > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenConvertDialog(advance)}
                                title="Convert Type"
                                data-testid={`convert-advance-${advance.id}`}
                              >
                                <RefreshCw className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                            {advance.status !== 'completed' ? (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleOpenDialog(advance)}
                                  title="Edit"
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteId(advance.id)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              // Super Admin can delete completed advances
                              isAdminLevel() && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => setDeleteId(advance.id)}
                                  className="text-destructive hover:text-destructive"
                                  title="Delete Completed Advance"
                                  data-testid={`delete-completed-${advance.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  {/* Totals Footer */}
                  <TableFooter>
                    <TableRow className="bg-muted/50 font-semibold">
                      <TableCell colSpan={3} className="text-right">TOTALS</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(totals.totalAmount)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(totals.totalDeducted)}</TableCell>
                      <TableCell className="text-right font-mono text-orange-600">{formatCurrency(totals.totalRemaining)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingAdvance ? 'Edit Advance' : 'Add New Advance'}</DialogTitle>
              <DialogDescription>
                {editingAdvance ? 'Update advance details' : 'Create a salary advance for an employee'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                  disabled={!!editingAdvance}
                >
                  <SelectTrigger data-testid="advance-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Advance Name *</Label>
                <Input
                  placeholder="e.g., Salary Advance - Jan 2026"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="advance-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={formData.advance_type}
                  onValueChange={(v) => setFormData({ ...formData, advance_type: v })}
                  disabled={!!editingAdvance}
                >
                  <SelectTrigger data-testid="advance-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADVANCE_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Total Amount *</Label>
                <Input
                  type="number"
                  placeholder="50000"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  min="1"
                  disabled={!!editingAdvance}
                  data-testid="advance-amount-input"
                />
              </div>

              {formData.advance_type === 'installment' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Deduction</Label>
                    <Input
                      type="number"
                      placeholder="5000"
                      value={formData.monthly_deduction}
                      onChange={(e) => setFormData({ ...formData, monthly_deduction: e.target.value })}
                      min="1"
                      data-testid="advance-monthly-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>OR Installments</Label>
                    <Input
                      type="number"
                      placeholder="10"
                      value={formData.num_installments}
                      onChange={(e) => setFormData({ ...formData, num_installments: e.target.value })}
                      min="1"
                      disabled={!!formData.monthly_deduction}
                      data-testid="advance-installments-input"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Month</Label>
                  <Select
                    value={formData.start_month}
                    onValueChange={(v) => setFormData({ ...formData, start_month: v })}
                    disabled={!!editingAdvance}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthOptions.map((m) => (
                        <SelectItem key={m.value} value={m.value.toString()}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Start Year</Label>
                  <Select
                    value={formData.start_year}
                    onValueChange={(v) => setFormData({ ...formData, start_year: v })}
                    disabled={!!editingAdvance}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={y.toString()}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  placeholder="Optional notes..."
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  data-testid="advance-remarks-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="advance-submit-btn">
                {submitting ? 'Saving...' : editingAdvance ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => { setDeleteId(null); setDeleteHasDeductions(false); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                {deleteHasDeductions && <AlertCircle className="h-5 w-5 text-amber-500" />}
                Delete Advance
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div>
                  {deleteHasDeductions ? (
                    <div className="space-y-2">
                      <p className="text-amber-600 dark:text-amber-400 font-medium">
                        ⚠️ This advance has existing deduction records!
                      </p>
                      <p>
                        Deleting this advance will also permanently remove all associated deduction history. 
                        This may affect salary reports where this advance was already deducted.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Are you sure you want to proceed with force delete?
                      </p>
                    </div>
                  ) : (
                    <span>Are you sure you want to delete this advance? This action cannot be undone.</span>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleDelete(deleteHasDeductions)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteHasDeductions ? 'Force Delete' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Convert Type Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-blue-600" />
                Convert Advance Type
              </DialogTitle>
              <DialogDescription>
                Change the deduction type for this advance
              </DialogDescription>
            </DialogHeader>
            {convertingAdvance && (
              <div className="space-y-4 py-4">
                {/* Current Info */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Current Advance</p>
                  <p className="font-medium">{convertingAdvance.name}</p>
                  <div className="flex justify-between mt-2 text-sm">
                    <span>Type: <Badge variant="outline">{convertingAdvance.advance_type?.replace('_', ' ')}</Badge></span>
                    <span>Remaining: <span className="font-mono text-orange-600">{formatCurrency(convertingAdvance.remaining_balance)}</span></span>
                  </div>
                </div>

                {/* Convert To */}
                <div className="space-y-2">
                  <Label>Convert To</Label>
                  <Select
                    value={convertData.advance_type}
                    onValueChange={(v) => setConvertData({ ...convertData, advance_type: v, monthly_deduction: '', num_installments: '' })}
                  >
                    <SelectTrigger data-testid="convert-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADVANCE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Installment Options */}
                {convertData.advance_type === 'installment' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Monthly Deduction</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 5000"
                        value={convertData.monthly_deduction}
                        onChange={(e) => setConvertData({ ...convertData, monthly_deduction: e.target.value, num_installments: '' })}
                        min="1"
                        data-testid="convert-monthly-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>OR Installments</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 10"
                        value={convertData.num_installments}
                        onChange={(e) => setConvertData({ ...convertData, num_installments: e.target.value, monthly_deduction: '' })}
                        min="1"
                        disabled={!!convertData.monthly_deduction}
                        data-testid="convert-installments-input"
                      />
                    </div>
                  </div>
                )}

                {convertData.advance_type === 'one_time' && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      <strong>Note:</strong> Converting to one-time will deduct the full remaining balance ({formatCurrency(convertingAdvance.remaining_balance)}) in the next salary cycle.
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleConvert} disabled={submitting} data-testid="convert-submit-btn">
                {submitting ? 'Converting...' : 'Convert'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
